import { CAPABILITIES } from "./permissions";
import type { Session } from "./session-context";

/**
 * Who may do what on a project, as the server decides it (test round R10, R10-UX1). The server is the authority; these
 * only decide whether a control is shown, so a Viewer is never offered a button the server refuses.
 *
 * Role test mode (Features__RoleTestMode): on the ONE project where a SystemAdmin acts as a role, the server drops the
 * SystemAdmin bypass (`EfPermissionResolver`), so every rule below asks `isSystemAdminIn`, never `session.isSystemAdmin`.
 * Other projects keep the admin's real powers.
 */

/** SystemAdmin powers on this project: not while role test mode replaces the admin's roles here. */
export function isSystemAdminIn(session: Pick<Session, "isSystemAdmin" | "testMode">, projectKey: string): boolean {
  const mode = session.testMode;
  return session.isSystemAdmin && !(mode?.active && mode.kind === "role" && mode.projectKey === projectKey);
}

/**
 * The caller's role codes on a project as the server's refusal reads them (`ProjectPermissions.Roles`): the project roles, plus
 * "DevOps" when an organisation DevOps grant covers the project (a grant with no project list covers all of them). Not on the
 * project where role test mode replaces the admin's roles with the test role. O-R15-1: picks a locked cell's words.
 */
export function rolesWithGrantIn(session: Pick<Session, "rolesIn" | "me" | "testMode">, projectKey: string): string[] {
  const roles: string[] = [...session.rolesIn(projectKey)];
  const mode = session.testMode;
  const testing = !!mode?.active && mode.kind === "role" && mode.projectKey === projectKey;
  const granted = session.me.orgRoles.some(
    (assignment) => assignment.role === "DevOps" && assignment.environments.length > 0 && (assignment.projectKeys == null || assignment.projectKeys.includes(projectKey)),
  );
  if (granted && !testing && !roles.includes("DevOps")) roles.push("DevOps");
  // Revision 110: acting as ATC (DevOps) or ATC (PMO) is a grant, not a project role (Me.projectRoles lists none there), but the
  // server's refusal reads it as the caller's role on this project.
  const grantRole = testing ? mode?.role : undefined;
  if ((grantRole === "DevOps" || grantRole === "ATC") && !roles.includes(grantRole)) roles.push(grantRole);
  return roles;
}

/**
 * The projects a cross-project ("some project") gate may count: every project `/auth/me` lists, except in role test mode, where
 * only the test project counts (ruling of 14 Oct 2026: View as shows exactly the viewed role's sidebar). The server already
 * narrows `/auth/me` that way (`TestModePolicy.CountsAcrossProjects`); this keeps the gates right against an older server too.
 */
export function crossProjectKeys(me: Pick<Session["me"], "projectRoles" | "projectPermissions" | "testMode">): string[] {
  const keys = new Set([...me.projectRoles.map((entry) => entry.projectKey), ...(me.projectPermissions ?? []).map((entry) => entry.projectKey)]);
  const mode = me.testMode;
  if (mode?.active && mode.kind === "role" && mode.projectKey) return keys.has(mode.projectKey) ? [mode.projectKey] : [];
  return [...keys];
}

/** Create an item of ANY type here (server: `item.create`, Settings → Permissions; by default any project role but bare Viewer).
 * The entry points that create one fixed type that is not an Issue or Action (sub-task, deployment, CR) ask this. */
export function mayCreateIn(session: Pick<Session, "can"> | null, projectKey: string): boolean {
  if (!session) return false;
  return session.can(projectKey, CAPABILITIES.itemCreate);
}

/** Revision 107 amendment: the work types the ATC group may create with `item.create_raid` alone. (The retired ISSUE_ACTION is
 * never offered for a new item.) */
export const RAID_CREATE_TYPES: readonly string[] = ["ISSUE", "ACTION"];

/** Create SOMETHING here: `item.create`, or `item.create_raid` (Issue and Action only). The generic Create entry points ask this. */
export function mayCreateSomethingIn(session: Pick<Session, "can"> | null, projectKey: string): boolean {
  if (!session) return false;
  return session.can(projectKey, CAPABILITIES.itemCreate) || session.can(projectKey, CAPABILITIES.itemCreateRaid);
}

/** The types of `all` the caller may create here, in its order: all of them with `item.create`; with `item.create_raid` alone,
 * only Issue and Action (the server refuses any other type, 403); none otherwise. */
export function creatableTypesIn<T extends string>(session: Pick<Session, "can">, projectKey: string, all: readonly T[]): T[] {
  if (session.can(projectKey, CAPABILITIES.itemCreate)) return [...all];
  if (session.can(projectKey, CAPABILITIES.itemCreateRaid)) return all.filter((type) => RAID_CREATE_TYPES.includes(type));
  return [];
}

/**
 * Create an item somewhere (the header's Create with no project chosen): a SystemAdmin, or `item.create` on any project —
 * one with a project role, or (0.10.37, R14-D01) any other project `/auth/me` reports codes for.
 */
export function mayCreateAnywhere(session: Pick<Session, "isSystemAdmin" | "me" | "can">): boolean {
  return session.isSystemAdmin || crossProjectKeys(session.me).some((projectKey) => mayCreateSomethingIn(session, projectKey));
}

/**
 * Rank the backlog and manage sprints — create, start, complete, move items into or out of one (server: `sprint.manage`).
 * Since the ruling of 2026-10-03 a locked row: the Project Director and Project Manager only, and a SystemAdmin.
 */
export function mayPlanIn(session: Pick<Session, "can">, projectKey: string): boolean {
  return session.can(projectKey, CAPABILITIES.sprintManage);
}

/**
 * Batch 16 (GRF-25): put an item into an open sprint, move it between sprints or take it out — the Sprint field on the create and
 * edit forms and "Move to sprint" (server: `sprint.assign`, which `sprint.manage` includes). By default the Chief Pilot, Capt., FO
 * and Analyst; never a partner or a Viewer.
 */
export function mayPutInSprintIn(session: Pick<Session, "can">, projectKey: string): boolean {
  return session.can(projectKey, CAPABILITIES.sprintAssign) || session.can(projectKey, CAPABILITIES.sprintManage);
}

/** The read-only DevOps queue permission (a workflow code, not a capability): Project manager and Project director by default. */
const DEVOPS_QUEUE_VIEW = "devops.queue.view";

/**
 * May open the DevOps queue (O-R12-1), the server's rule in `DevOpsQueueService`: a DevOps organisation grant (acts on its
 * environments), or `devops.queue.view` through a role on some project (read-only). Nobody else, a SystemAdmin included —
 * the page would only say "You need DevOps access…", so the side nav hides the entry. An older server that sends no
 * `projectPermissions` falls back to the default holders (Project manager, Project director, ATC (Infra)).
 */
export function mayOpenDevOpsQueue(session: Pick<Session, "isPartner" | "me" | "can">): boolean {
  if (session.isPartner) return false;
  const { me } = session;
  if (me.orgRoles.some((assignment) => assignment.role === "DevOps" && assignment.environments.length > 0)) return true;
  const counted = new Set(crossProjectKeys(me));
  return me.projectRoles.filter((entry) => counted.has(entry.projectKey)).some((entry) =>
    me.projectPermissions
      ? session.can(entry.projectKey, DEVOPS_QUEUE_VIEW)
      : // Revision 107: ATC (Infra) holds devops.queue.view by default too (it keeps the DevOps column's codes).
        entry.roles.some((role) => role === "ProjectManager" || role === "ProjectDirector" || role === "InfraManager"),
  );
}

/** The two approval steps of a deployment (workflow codes, not capabilities): Capt. and Chief Pilot by default. */
const APPROVAL_CODES: readonly string[] = ["deployment.approve_pm", "deployment.approve_director"];

/**
 * Batch 20 B20-1 (sidebar by role): may see the Approvals entry — a cross-project page (`/me/approvals`), so "any project": the
 * caller holds `deployment.approve_pm` or `deployment.approve_director` on some project, the same codes the server's queue lists
 * steps for (`DeploymentQueues.Approvals`). Never a partner. An older server that sends no `projectPermissions` falls back to the
 * default holders (Project manager, Project director). A workflow code is never implied by System Admin here, as on the server.
 */
export function mayOpenApprovals(session: Pick<Session, "isPartner" | "me" | "can">): boolean {
  if (session.isPartner) return false;
  const { me } = session;
  return crossProjectKeys(me).some((projectKey) =>
    me.projectPermissions
      ? APPROVAL_CODES.some((code) => session.can(projectKey, code))
      : (me.projectRoles.find((entry) => entry.projectKey === projectKey)?.roles ?? []).some((role) => role === "ProjectManager" || role === "ProjectDirector"),
  );
}

/**
 * Batch 26 R1: may open Status reports — holds `status.view` on some project (a Capt. or Chief Pilot there, or a System Admin).
 * The server lists only the projects the caller may read, and refuses a partner (404).
 */
export function mayOpenStatusReports(session: Pick<Session, "isPartner" | "me" | "can">): boolean {
  if (session.isPartner) return false;
  const { me } = session;
  return crossProjectKeys(me).some((projectKey) => session.can(projectKey, "status.view"));
}

/**
 * Batch 20 B20-3: may start a deployment or add cards to one on this project — "New deployment" on Deployments and "Add selected
 * to deployment…" on the Deployment backlog. The server asks `deployment.request` for the deployment's details and scope
 * (`DeploymentActions`: edit_detail, scope_change), and the project leads' override does not cover them, so a System Admin needs it
 * through a project role like anyone else (`session.can` never implies a workflow code). New deployment also creates an item
 * (`item.create`, asked separately). Never a partner (the backlog is internal; partners create no deployment from these pages).
 */
export function mayRequestDeploymentIn(session: Pick<Session, "isPartner" | "me" | "rolesIn" | "can">, projectKey: string): boolean {
  if (session.isPartner || projectKey === "") return false;
  // An older server that sends no `projectPermissions`: the default holders (reference.sql; the Analyst since batch 20 B20-2).
  if (!session.me.projectPermissions) return session.rolesIn(projectKey).some((role) => DEPLOYMENT_REQUESTERS.includes(role));
  return session.can(projectKey, "deployment.request");
}

/** The default holders of `deployment.request` among the internal project roles (reference.sql, batch 20 B20-2). */
const DEPLOYMENT_REQUESTERS: readonly string[] = ["Member", "QA", "Analyst", "ProjectManager", "ProjectDirector"];

/** What the item read says this caller may do (`WorkItem.permissions`, R10-D03). */
export interface ItemRights {
  /** Edit and Assign to me. */
  canEdit: boolean;
  /**
   * Any write that is not an edit (false only for a Viewer; partners included). Since R12-D02 no control is gated on it:
   * links, attachments and Log in timesheet each follow their own flag below. It still picks the Viewer's read-only line.
   */
  canWrite: boolean;
  /** A public comment on THIS item. On a ticket only the reply roles and partners (ruling 2026-10-02, R11). */
  canComment: boolean;
  canAddInternalNote: boolean;
  /** Reply to the requester on a ticket (the service desk's answering roles). */
  canReplyToRequester: boolean;
  /** 0.10.36: delete the item (`item.delete`). */
  canDelete: boolean;
  /** 0.10.36: move the item to another project or type (`item.move`). */
  canMove: boolean;
  /** 0.10.36: add and remove links (`item.link`). */
  canLink: boolean;
  /** 0.10.36: upload attachments, change their category (`item.attach`). */
  canAttach: boolean;
  /** 0.10.36: Log in timesheet (`item.log_time`). */
  canLogTime: boolean;
  /** 0.10.36: delete a comment someone else wrote (`item.comment_delete`). */
  canDeleteComments: boolean;
}

/**
 * `WorkItem.permissions` as rights; absent (an older server) reads as allowed, and the server still refuses. `canWrite` is
 * contract 0.10.34; a 0.10.33 server sends only `canComment`, which then meant the same thing. The 0.10.36 flags fall back
 * to `canWrite` (links, attachments, Log in timesheet) or to allowed (delete, move, comment delete), where the screens used
 * their own role checks before.
 */
export function itemRights(item: { permissions?: Partial<Record<keyof ItemRights, boolean>> | null }): ItemRights {
  const p = item.permissions;
  const canWrite = p?.canWrite ?? p?.canComment ?? true;
  return {
    canEdit: p?.canEdit ?? true,
    canWrite,
    canComment: p?.canComment ?? true,
    canAddInternalNote: p?.canAddInternalNote ?? true,
    canReplyToRequester: p?.canReplyToRequester ?? true,
    canDelete: p?.canDelete ?? true,
    canMove: p?.canMove ?? true,
    canLink: p?.canLink ?? canWrite,
    canAttach: p?.canAttach ?? canWrite,
    canLogTime: p?.canLogTime ?? canWrite,
    canDeleteComments: p?.canDeleteComments ?? true,
  };
}
